<script>
import { mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
    props: {
        staffId: {
            type: String
        },
        resetCreating: {
            type: Function
        },
        refreshList: {
            type: Function
        }
    },
    // Add CSS for animations and styling - identical to createOverrideModal
    head() {
      return {
        style: [
          {
            cssText: `
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              
              @keyframes slideDown {
                from { 
                  opacity: 0;
                  transform: translateY(-10px);
                }
                to { 
                  opacity: 1;
                  transform: translateY(0);
                }
              }
              
              .dropdown-animation {
                animation: slideDown 0.2s ease-out;
              }
              
              .overlay-animation {
                animation: fadeIn 0.2s ease-in-out;
              }
              
              /* Multiselect error styling */
              .multiselect-error .multiselect__tags {
                border-color: #ef4444 !important;
                border-width: 1px !important;
              }
              
              .multiselect-error .multiselect__input:focus {
                box-shadow: 0 0 0 1px #ef4444 !important;
                outline: none !important;
              }
              
              /* Fix multiselect dark mode */
              .dark .multiselect__tags {
                background-color: #334155 !important;
                color: white !important;
              }
              
              .dark .multiselect__input,
              .dark .multiselect__single {
                background-color: #334155 !important;
                color: white !important;
              }
              
              .dark .multiselect__content-wrapper {
                background-color: #1e293b !important;
                color: white !important;
              }
              
              .dark .multiselect__option {
                color: #e2e8f0 !important;
              }
              
              .dark .multiselect__option--highlight {
                background-color: #0ea5e9 !important;
                color: white !important;
              }
            `
          }
        ]
      };
    },
    data() {
        return {
            date: null,
            slots: [],
            timeOptions: [],
            isSubmitting: false,
            errors: {
              date: false,
              slots: [],
              reason: false,
            },
            reason: ''
        }
    },
    computed: {
      today() {
        return new Date().toISOString().split('T')[0];
      }
    },
    mounted() {
      this.generateTimeOptions();
      this.addDefaultTimeSlot();
    },
    methods: {
      ...mapActions('appointments', ['createStaffUnavailability']),

      generateTimeOptions() {
        let slots = [];
        let start = 0;
        let end = 24 * 60;

        for (let minutes = start; minutes < end; minutes += 15) {
          let hours = Math.floor(minutes / 60);
          let mins = minutes % 60;
          let ampm = hours >= 12 ? "PM" : "AM"; // TODO: i18n
          let formattedHour = hours % 12 || 12;

          slots.push({
            display: `${formattedHour}:${String(mins).padStart(2, "0")} ${ampm}`,
            value: `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`
          });
        }

        this.timeOptions = slots;
      },

      addDefaultTimeSlot() {
        const nineAM = this.timeOptions.find(option =>
          option.value === '09:00:00' || option.value === '09:00'
        );

        const fivePM = this.timeOptions.find(option =>
          option.value === '17:00:00' || option.value === '17:00'
        );

        if (nineAM && fivePM) {
          this.slots.push({
            startTime: nineAM,
            endTime: fivePM
          });
        } else {
          console.warn('Default time options not found, using first available options'); // TODO: i18n

          if (this.timeOptions.length >= 2) {
            let morningOption = this.timeOptions.find(t => t.value.startsWith('09:')) || this.timeOptions[32];
            let eveningOption = this.timeOptions.find(t => t.value.startsWith('17:')) || this.timeOptions[68];

            this.slots.push({
              startTime: morningOption,
              endTime: eveningOption
            });
          }
        }
      },

      validateForm() {
        this.errors = {
          date: false,
          slots: [],
          reason: false
        };

        let isValid = true;

        if (!this.date) {
          this.errors.date = true;
          isValid = false;
        }

        if (!this.reason || this.reason.length == 0) {
          this.errors.reason = true;
          isValid = false;
        }

        this.slots.forEach((slot, index) => {
          const slotErrors = {};

          if (!slot.startTime) {
            slotErrors.startTime = true;
            isValid = false;
          }

          if (!slot.endTime) {
            slotErrors.endTime = true;
            isValid = false;
          }

          if (slot.startTime && slot.endTime) {
            const startTime = slot.startTime.value;
            const endTime = slot.endTime.value;

            if (startTime >= endTime) {
              slotErrors.timeOrder = true;
              isValid = false;
            }
          }

          this.errors.slots[index] = slotErrors;
        });

        return isValid;
      },

      async handleCreate() {
        if (!this.validateForm()) {
          useAlert(this.$t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.VALIDATION_ERROR'));
          return;
        }

        this.isSubmitting = true;

        try {
          const apiSlots = this.slots.map(slot => ({
            start_time: slot.startTime.value,
            end_time: slot.endTime.value
          }));

          const payload = {
            // "date": this.date,
            // "slots": apiSlots,
            "unavailable_date": this.date,
            "reason": this.reason,
            "start_time": this.slots[0].startTime.value,
            "end_time": this.slots[0].endTime.value
          };

          await this.createStaffUnavailability({
            staffId: this.staffId,
            payload,
          });

          useAlert(this.$t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.SUCCESS_MESSAGE'));
          this.refreshList();
          this.resetCreating();
        } catch (err) {
          console.error(err);
          useAlert(err.message || this.$t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.ERROR_MESSAGE'));
        } finally {
          this.isSubmitting = false;
        }
      }
  }
}
</script>

<template>
  <div class="p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-w-md w-full mx-auto">
    <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
      {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.TITLE') }}
    </h2>

    <label class="block mb-5">
      <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
        {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.REASON') }} <span class="text-red-500">*</span>
      </span>
      <input 
        type="text" 
        v-model="reason" 
        :min="today"
        :class="[
          'mt-1 w-full min-w-[250px] p-3 border rounded-md text-gray-900 dark:text-white dark:bg-slate-700 dark:border-gray-600 focus:outline-none',
          errors.reason ? 'border-red-500 focus:ring-red-500' : 'focus:ring-woot-500'
        ]"
        :placeholder="$t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.REASON_PLACEHOLDER')"
      />
      <p v-if="errors.reason" class="mt-1 text-sm text-red-500">
        {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.REASON_REQUIRED') }}
      </p>
    </label>

    <!-- Date Picker -->
    <label class="block mb-5">
      <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
        {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.SELECT_DATE') }} <span class="text-red-500">*</span>
      </span>
      <input 
        type="date" 
        v-model="date" 
        :min="today"
        :class="[
          'mt-1 w-full min-w-[250px] p-3 border rounded-md text-gray-900 dark:text-white dark:bg-slate-700 dark:border-gray-600 focus:outline-none',
          errors.date ? 'border-red-500 focus:ring-red-500' : 'focus:ring-woot-500'
        ]"
      />
      <p v-if="errors.date" class="mt-1 text-sm text-red-500">
        {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.SELECT_DATE_ERROR') }}
      </p>
    </label>

    <!-- <div class="flex justify-end w-full mb-4">
      <woot-button 
        size="small"
        variant="smooth"
        @click="addDefaultTimeSlot"
      >
        {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.ADD_SLOT') }}
      </woot-button>
    </div> -->

    <div v-for="(slot, index) in slots" :key="index" class="mb-8"
      :class="{'border-b border-gray-200 dark:border-gray-700 pb-4' : slots.length > 1 && index < slots.length - 1}">
      <div class="flex items-center gap-4">
        <!-- Start Time Picker -->
        <div class="block flex-grow">
          <span v-if="index == 0" class="text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.START_TIME') }} <span class="text-red-500">*</span>
          </span>
          <div class="mt-1 relative">
            <multiselect 
              v-model="slot.startTime"
              :options="timeOptions"
              label="display"
              track-by="value"
              :searchable="true"
              :close-on-select="true"
              :show-labels="false"
              :multiple="false"
              :class="[
                'w-full text-gray-900 dark:text-white dark:bg-slate-700',
                errors.slots[index] && errors.slots[index].startTime ? 'multiselect-error' : ''
              ]"
              :placeholder="$t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.SELECT_START_TIME')"
            />
          </div>
          <p v-if="errors.slots[index] && errors.slots[index].startTime" class="mt-1 text-sm text-red-500">
            {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.START_TIME_REQUIRED') }}
          </p>
        </div>

        <span>-</span>

        <!-- End Time Picker -->
        <div class="block flex-grow">
          <span v-if="index == 0" class="text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.END_TIME') }} <span class="text-red-500">*</span>
          </span>
          <div class="mt-1 relative">
            <multiselect 
              v-model="slot.endTime"
              :options="timeOptions"
              label="display"
              track-by="value"
              :searchable="true"
              :close-on-select="true"
              :show-labels="false"
              :multiple="false"
              :class="[
                'w-full text-gray-900 dark:text-white dark:bg-slate-700',
                errors.slots[index] && errors.slots[index].endTime ? 'multiselect-error' : ''
              ]"
              :placeholder="$t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.SELECT_END_TIME')"
            />
          </div>
          <p v-if="errors.slots[index] && errors.slots[index].endTime" class="mt-1 text-sm text-red-500">
            {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.END_TIME_REQUIRED') }}
          </p>
        </div>

        <button 
          v-if="slots.length > 1" 
          @click="slots.splice(index, 1)"
          type="button"
          class="self-start"
          :class="{'mt-9': index === 0, 'mt-3': index > 0}"
        >
          <fluent-icon 
            icon="delete" 
            class="text-red-500 hover:text-red-700 cursor-pointer"
          />
        </button>
      </div>
      
      <p v-if="errors.slots[index] && errors.slots[index].timeOrder" class="mt-2 text-sm text-red-500 text-center">
        {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.TIME_ORDER_ERROR') }}
      </p>
    </div>
    
    <div class="flex justify-between gap-8">
      <button
        type="button"
        class="w-full py-3 mt-2 text-white font-medium rounded-md transition bg-slate-600 hover:bg-slate-700 focus:ring-2 focus:ring-slate-400 focus:outline-none"
        @click="resetCreating()"
        :disabled="isSubmitting"
      >
        {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.CANCEL') }}
      </button>
      <button 
        type="button"
        @click="handleCreate()"
        :disabled="isSubmitting"
        class="w-full py-3 mt-2 text-white font-medium rounded-md transition 
          bg-woot-600 hover:bg-woot-700 focus:ring-2 focus:ring-woot-400 focus:outline-none
          disabled:opacity-70 disabled:cursor-not-allowed"
      >
        <span v-if="isSubmitting" class="flex items-center justify-center">
          <span class="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></span>
          {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.SUBMITTING') }}
        </span>
        <span v-else>
          {{ $t('BRANCHES.CREATE_UNAVAILABILITY_MODAL.CREATE') }}
        </span>
      </button>
    </div>
  </div>
</template>
